/**
 * Prioritization App - Main Router (v3.0)
 *
 * Navigation aligned with Operating Domains (Item Management → Strategy → Cycles & Execution):
 *
 * /prioritization
 * ├── (home) - Dashboard
 * ├── /my-work - My work (/my-tasks redirects here)
 * ├── /intake - Item Management: Intake
 * │   ├── /submit
 * │   ├── /mine
 * │   └── /triage
 * ├── /items - Item Management: Items (and /shape, /scoring, /leveling)
 * ├── /strategy - Strategy (discovery and alignment)
 * │   ├── /opportunity-tree - Opportunity Solution Tree (OST)
 * │   ├── /objectives - Strategic objectives (define and manage)
 * │   ├── /alignment - Strategy alignment (objectives, gaps)
 * │   ├── /decisions - Decision history
 * │   ├── /list - Strategy list
 * │   └── /:strategyId - Strategy detail
 * ├── /cycles - Cycles & Tracks
 * │   └── /:cycleId - Cycle (tracks, readiness, sessions)
 * ├── /catchball - Catchball (loops)
 * ├── /priorities - Published Priority Lists
 * ├── /plans - Deprecated (DEP-020) → redirects to plan-of-record / planning plans
 * ├── /plan-of-record/* - Organizational instrument (PoR) under this app shell
 * ├── /settings - Admin configuration
 * └── /documentation
 *
 * Legacy routes (/shape, /scoring, /leveling, /sessions, /lists, /tools, etc.)
 * are redirected to canonical paths where applicable.
 */

import { Routes, Route, Navigate, useLocation, useParams, useSearchParams, Outlet } from 'react-router-dom';
import { ToastProvider } from '../../components/ui/Toast';
import { Loader2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useFeatureFlag, areUserFlagsLoaded, FEATURE_FLAGS } from '../../lib/featureFlags';
import { useWorkspaceModules, isModuleEnabled, isModuleFeatureAccessible, useWorkspaceModulesReady } from '../../lib/workspaceModulesService';
import { useModuleGate } from '../../lib/moduleGate';
import { useDecisionLatencyEnabled } from '../../hooks/useDecisionLatencyEnabled';
import { ExchangeContextFrame } from '../../components/prioritization/catchball/ExchangeContextBanner';
import { RankModeBand } from '../../components/prioritization/RankModeBand';
import { CycleVersionFlags } from '../../components/prioritization/CycleVersionFlags';
import SessionsSurface from './sessions/SessionsSurface';

/** Redirect legacy /prioritization/item/:id to canonical /prioritization/items/:id */
function RedirectCycleAggregationToResults() {
  const { cycleId } = useParams<{ cycleId: string }>();
  return <Navigate to={cycleId ? `/prioritization/cycles/${cycleId}/results` : '/prioritization/cycles'} replace />;
}

function RedirectItemToItemsDetail() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/prioritization/items/${id}` : '/prioritization/items'} replace />;
}


function RedirectLegacyAttributePermissionDetail() {
  const { attributeKey } = useParams<{ attributeKey: string }>();
  return (
    <Navigate
      to={
        attributeKey
          ? `/prioritization/settings/attribute-permissions/${encodeURIComponent(attributeKey)}`
          : '/prioritization/settings/attribute-permissions'
      }
      replace
    />
  );
}
import PrioritizationLayout from '../../components/prioritization/Layout';

// ============================================================================
// Catch-all Redirect Handler
// ============================================================================

function CatchAllRedirect() {
  const location = useLocation();
  const currentPath = location.pathname;
  
  // v2.2 - Canonical paths (primary navigation)
  const validPrefixes = [
    '/prioritization/dashboard',
    '/prioritization/intake',
    '/prioritization/items/triage',
    '/prioritization/items/needs-definition',
    '/prioritization/items/types',
    '/prioritization/items/readiness',
    '/prioritization/items',
    '/prioritization/relationships',
    '/prioritization/collections',
    '/prioritization/cycles',
    '/prioritization/cycle-design',
    '/prioritization/governance',
    '/prioritization/tracks',  // Track Detail (participation, pool, criteria, values, sessions) — build contract: UI must be reachable
    '/prioritization/catchball',
    '/prioritization/catchball/exchange',
    '/prioritization/catchball/executive-inbox',
    '/prioritization/commitments',
    '/prioritization/priorities',
    '/prioritization/scoreboard',
    '/prioritization/plans',
    '/prioritization/plan-of-record',
    '/prioritization/decisions',
    '/prioritization/decisions/',  // one decision's page (/prioritization/decisions/:decisionId)
    '/prioritization/decisions/brief',
    '/prioritization/decision-intelligence',
    '/prioritization/provenance',
    '/prioritization/my-work',
    '/prioritization/my-tasks',
    '/prioritization/task/',
    '/prioritization/notifications',
    '/prioritization/pulse',
    '/prioritization/settings',
    '/prioritization/settings/sandbox',
    '/prioritization/documentation',
    '/prioritization/item/',
    // Legacy paths (still work but redirect or serve legacy pages)
    '/prioritization/shape',
    '/prioritization/scoring',
    '/prioritization/leveling',
    '/prioritization/sessions',
    '/prioritization/sessions/',
    '/prioritization/loops',
    '/prioritization/lists',
    '/prioritization/tools/aggregation-demo',
    '/prioritization/tools',
    '/prioritization/monitor',
    '/prioritization/strategy',
    '/prioritization/reports',
    '/prioritization/tournaments',
    '/prioritization/search',
    '/prioritization/lexicon',
    '/prioritization/workspace',
    '/prioritization/processes',
    '/prioritization/admin',
  ];
  
  const isValidPath = validPrefixes.some(prefix => currentPath.startsWith(prefix));
  
  // Check for duplicate segments
  const hasDuplicates = /\/(\w+)\/\1/.test(currentPath);
  
  if (hasDuplicates) {
    // Fix duplicates
    const fixedPath = currentPath.replace(/\/(\w+)\/\1+/g, '/$1');
    return <Navigate to={fixedPath} replace />;
  }
  
  if (isValidPath) {
    return null;
  }
  
  return <Navigate to="/prioritization" replace />;
}

// ============================================================================
// Page Imports
// ============================================================================

// Dashboard
import { ContributorHomeRedirect, ContributorAwareDashboard } from '../../components/prioritization/ContributorHomeRedirect';
import DemoMode from '../DemoMode';
import { ItemListView } from '../../components/prioritization/item-list';
import { ItemDetailPage } from './ItemDetailPage';
import { ItemPrintPage } from './ItemPrintPage';
import { StrategyCanvasPrintPage } from './strategy/StrategyCanvasPrintPage';
import { V2momPrintPage } from './strategy/v2mom/V2momPrintPage';
import { MemoPrintPage } from './sessions/MemoPrintPage';
import MyWorkPage from '../../features/my-work/MyWorkPage';
import { NotificationCenter } from '../../components/prioritization/NotificationCenter';
import { TaskRunner } from '../../components/prioritization/tasks';

// Intake pages
import SubmitIdea from './SubmitIdea';
import IntakeEmbedPage from './IntakeEmbedPage';
import MySubmissions from './MySubmissions';
import IntakeQueue from './IntakeQueue';
import StakeholderPulsePage from './StakeholderPulsePage';

// Shape pages
import { ItemShaping } from '../../components/prioritization/spaces/ItemShaping';
import { AttributeManagement } from '../../components/prioritization/spaces/AttributeManagement';
import CustomFields from './settings/CustomFields';
import ItemHierarchy from './ItemHierarchy';
import RelationshipItemsPage from './RelationshipItemsPage';
import RelationshipGraph from './RelationshipGraph';
import RelationshipHealthPage from './RelationshipHealthPage';
import RelationshipsHomePage from './RelationshipsHomePage';
import CollectionsPage from './CollectionsPage';
import CommitmentRegistryPage from './CommitmentRegistryPage';
import CommitmentDetailPage from './CommitmentDetailPage';
import CycleDesignCollectionsPage from './CycleDesignCollectionsPage';
import OpenQuestionsPage from './OpenQuestionsPage';
import BackplanList from '../backplan/BackplanList';
import BackplanEditor from '../backplan/BackplanEditor';
import OutcomeDriversPage from './OutcomeDriversPage';
import NewOutcomeDriverPage from './NewOutcomeDriverPage';
import DesiredOutcomesList from '../desired-outcomes/DesiredOutcomesList';
import DesiredOutcomeEditor from '../desired-outcomes/DesiredOutcomeEditor';

// Cycle pages
import ProcessList from '../processes/ProcessList';
import CycleDetail from '../processes/CycleDetail';
import { CycleSetupWizard } from './cycles/CycleSetupWizard';
import { CycleDesignerWizard } from '@/features/cycle-designer';
import { DesignerErrorFallback } from '@/features/cycle-designer/components/DesignerErrorFallback';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { shouldUseCycleDesigner } from '@/features/cycle-designer/lib/cycleDesignerPhase';
import { useCycleDesignerEnabled } from '@/features/cycle-designer/hooks/useCycleDesignerEnabled';
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore';
import { useCatchballLoopStore } from '../../stores/prioritization/catchballLoopStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { isAdminRole } from '../../types/prioritization/roles';
import { ModuleOffNotice } from '../../components/shared/ModuleOffNotice';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { useUserCycleRoleContext } from '../../hooks/prioritization/useUserCycleRoleContext';
import { canSync } from '../../lib/permissionResolver';
import { shouldUseSetupWizard } from '../../lib/cycleSetupPhase';
import type { CatchballLoop } from '../../types/prioritization/catchballLoop';
import CycleDesign from './CycleDesign';
import ProposalsPage from './governance/ProposalsPage';
import PriorityProposalReviewPage from './governance/PriorityProposalReviewPage';
import RatificationPage from './governance/RatificationPage';

// Item Pool pages
import { ItemPoolEditor } from '../../components/prioritization/spaces/ItemPoolEditor';

// Monitor pages
import { DriftDetection } from '../../components/prioritization/spaces/DriftDetection';
import { FocusMap } from '../../components/prioritization/spaces/FocusMap';
import { Analytics } from '../../components/prioritization/spaces/Analytics';

// Strategy pages
import StrategyAlignment from '../StrategyAlignment';
import ObjectivesPage from './strategy/ObjectivesPage';
import GoalsPage from './strategy/GoalsPage';
import DecisionHistory from '../DecisionHistory';

// Provenance + Decisions
import DecisionsPage from './DecisionsPage';
import WristbandPage from './WristbandPage';
import DecisionDetailPage from './DecisionDetailPage';
import DecisionBriefPage from './DecisionBriefPage';
import { DecisionIntelligence } from '../DecisionIntelligence';
import { DecisionRecords } from '../DecisionRecords';
import CognitiveBiasPage from './insights/CognitiveBiasPage';
import JudgmentIndexPage from './insights/JudgmentIndexPage';
import JudgmentAnalyticsPage from './insights/JudgmentAnalyticsPage';
import LineagePage from './LineagePage';

import { ItemJourneyPage } from './ItemJourneyPage';
import { WorkspaceJourneyPage } from './WorkspaceJourneyPage';
import OpportunitySolutionTree from '../opportunity-solution-tree/OpportunitySolutionTree';

// Catchball pages
import ObjectiveInboxPage from './catchball/ObjectiveInbox';
import ProposalReviewPage from './catchball/ProposalReviewPage';
import RequestComposerPage from './catchball/RequestComposerPage';
import { ExecutiveReconciliationPage } from './catchball/ExecutiveReconciliationPage';
import ExecutiveInboxPage from './catchball/ExecutiveInboxPage';
import { CatchballExchangeLandingPage } from './catchball/exchange/CatchballExchangeLandingPage';
import { CatchballExchangeCyclePage } from './catchball/exchange/CatchballExchangeCyclePage';
import { CatchballSetupPage } from './catchball/CatchballSetupPage';
import { CatchballInboxPage } from './catchball/CatchballInboxPage';
import { buildCatchballInboxUrl } from '../../lib/catchballInboxUrls';

// Wrapper to extract cycleId from route params
function ObjectiveInboxWithParams() {
  const { cycleId } = useParams<{ cycleId: string }>();
  if (!cycleId) return null;
  return <ObjectiveInboxPage cycleId={cycleId} />;
}

/** DEP-025: Ball Detail as primary → Catchball (history remains secondary on Desk). */
/**
 * Legacy /prioritization/catchball/* → /prioritization/cascade/*
 *
 * This redirect is PERMANENT, not transitional. `notifications.action_url` is a
 * persisted column and catchball URLs are written into it — there are live rows
 * in production pointing at /prioritization/catchball/desk/... and
 * /prioritization/catchball/proposal/.... Dropping the old tree would break real
 * notification links for real people. The same reasoning already keeps
 * /catchball/workbench/... alive.
 *
 * Two segments moved rather than merely relocating:
 *   exchange            → the cascade root
 *   exchange/:cycleId   → :cycleId
 *   desk/:c/:l          → catchball/:c/:l
 * Everything else keeps its segment and simply changes parent.
 */
function LegacyCatchballRedirect() {
  const params = useParams();
  const [search] = useSearchParams();
  const rest = params['*'] ?? '';
  const segments = rest.split('/').filter(Boolean);

  let mapped = segments;
  if (segments[0] === 'exchange') {
    mapped = segments.slice(1); // exchange → root, exchange/:cycleId → :cycleId
  } else if (segments[0] === 'desk') {
    mapped = ['catchball', ...segments.slice(1)];
  }

  const qs = search.toString();
  const to = `/prioritization/cascade${mapped.length ? `/${mapped.join('/')}` : ''}${qs ? `?${qs}` : ''}`;
  return <Navigate to={to} replace />;
}

/** Retired catchball surfaces → the cycle's exchange hub. */
function RetiredToCycleExchange() {
  const { cycleId } = useParams<{ cycleId: string }>();
  return (
    <Navigate
      to={cycleId ? `/prioritization/catchball/exchange/${cycleId}` : '/prioritization/catchball/exchange'}
      replace
    />
  );
}

/**
 * Ball record URL → Catchball, unconditionally.
 *
 * This used to fall through to BallDetailPage when the caller passed
 * `?from=desk`. That page made no state changes — it was a read-only record
 * view — and the only link that set the flag was on the Desk itself, pointing
 * back at a lesser version of what the Desk already shows. Both are retired
 * (2026-08-28); the URL still resolves so old deep links do not 404.
 */
function BallDetailToDeskRedirect() {
  const { cycleId, loopId, ballId } = useParams<{
    cycleId: string;
    loopId: string;
    ballId: string;
  }>();
  if (!cycleId || !loopId) {
    return <Navigate to="/prioritization/catchball/exchange" replace />;
  }
  return (
    <Navigate to={buildCatchballInboxUrl(cycleId, loopId, { ballId })} replace />
  );
}

/** DEP-025: Exchange single-loop URL → Catchball. */
function CatchballExchangeLoopToDeskRedirect() {
  const { cycleId, loopId } = useParams<{ cycleId: string; loopId: string }>();
  if (!cycleId || !loopId) {
    return <Navigate to="/prioritization/catchball/exchange" replace />;
  }
  return <Navigate to={buildCatchballInboxUrl(cycleId, loopId)} replace />;
}

/**
 * v2.3: Redirect legacy phase-based routes to cycle overview.
 * Work now happens at the Track level, not cycle-global phases.
 */
function CyclePhaseRedirect() {
  const { cycleId } = useParams<{ cycleId: string }>();
  return <Navigate to={`/prioritization/cycles/${cycleId}`} replace />;
}

/** DEP-020: /prioritization/plans/:id → Planning plan-cycle home */
function RedirectDeprecatedPrioritizationPlanCycle() {
  const { planCycleId } = useParams<{ planCycleId: string }>();
  return <Navigate to={`/planning/plans/${planCycleId}`} replace />;
}

function CatchballDashboardRedirect() {
  const { cycleId } = useParams<{ cycleId: string }>();
  return <Navigate to={`/prioritization/catchball/dashboard/${cycleId}`} replace />;
}

// Settings pages
import AdminUsers from './admin/AdminUsers';
import UserPermissionsReport from './admin/UserPermissionsReport';
import AdminRoles from './admin/AdminRoles';
import RolePackages from './settings/RolePackages';
import AdminGroups from './admin/AdminGroups';
import AdminInvites from './admin/AdminInvites';
import AdminIdentity from './admin/AdminIdentity';
import AdminOrgHierarchy from './admin/AdminOrgHierarchy';
import Connectors from './settings/Connectors';
import AccountabilityTrail from './settings/AccountabilityTrail';
import WorkspaceAuditLog from './settings/WorkspaceAuditLog';
import OnboardingFunnel from './settings/OnboardingFunnel';
import ExperienceProfiles from './settings/ExperienceProfiles';
import ExperienceAudit from './settings/ExperienceAudit';
import FeaturedRolesSettings from '../../features/home/FeaturedRolesSettings';
import SyncEventLog from './settings/SyncEventLog';
import AmbientRuntime from './settings/AmbientRuntime';
import Automations from './settings/Automations';
import Excursions from './settings/Excursions';
import ProductHierarchy from './settings/ProductHierarchy';
import ConflictQueue from './settings/ConflictQueue';
import SyncHealth from './settings/SyncHealth';
import EntityLinker from './settings/EntityLinker';
import FieldMappings from './settings/FieldMappings';
import CsvCitizenshipSettings from './settings/CsvCitizenshipSettings';
import ApiKeys from './settings/ApiKeys';
import WorkspaceStatus from './settings/WorkspaceStatus';
import WorkspacePlanPage from './settings/WorkspacePlanPage';
import Policies from './settings/Policies';
import Permissions from './settings/Permissions';
import { SpacesSettings } from './settings/SpacesSettings';
import Workflows from './settings/Workflows';
import WorkflowSetupWizard from './settings/WorkflowSetupWizard';
import { TrackSettings } from './settings/TrackSettings';
import Templates from './settings/Templates';
import TemplateSetupWizard from './settings/TemplateSetupWizard';
import Queues from './settings/Queues';
import DataDiagnostics from './settings/DataDiagnostics';
import BacklogHealth from './settings/BacklogHealth';
import ArchivedItems from './settings/ArchivedItems';
import AttributePermissions from './settings/AttributePermissions';
import AttributePermissionDetail from './settings/AttributePermissionDetail';
import AttributeDetail from './settings/AttributeDetail';
import ValueScalesSettings from './settings/ValueScalesSettings';
import SectorMap from './settings/SectorMap';
import AuthorizationDemo from './settings/AuthorizationDemo';
import AirTrafficController from './settings/AirTrafficController';
import ValueEstimatorAgent from './settings/ValueEstimatorAgent';
import AgentsPage from './settings/AgentsPage';
import VoicesPage from './settings/VoicesPage';
import VoiceDetailPage from './settings/VoiceDetailPage';
import CustomAgentsPage from './settings/CustomAgentsPage';
import CustomAgentDetailPage from './settings/CustomAgentDetailPage';
import AiAssistsPage from './settings/AiAssistsPage';
import ClassificationProposalsReport from './settings/ClassificationProposalsReport';
import TwinsAgent from './settings/TwinsAgent';
import AgentReportPage, { LegacyEstimatorReportRedirect } from './AgentReportPage';
import ScribePage from './ScribePage';
import ScribeAgent from './settings/ScribeAgent';
import HarryAgent from './settings/HarryAgent';
import WesAgent from './settings/WesAgent';
import NexusAgent from './settings/NexusAgent';
import DecisionLatencyReportPage from './DecisionLatencyReportPage';
import { AllFindings } from './AllFindings';
import { TemplateAgentSettings } from '../../components/agents/TemplateAgentSettings';
import { BEACON_AGENT, CASSANDRA_AGENT, COMPASS_AGENT, KEYSTONE_AGENT, MERIDIAN_AGENT, CONDITION_WATCH_AGENT, REVIEW_DUE_AGENT } from '../../lib/ai/agentDefinitions';
import CycleConfig from './CycleConfig';
import { CycleResults } from './cycles/CycleResults';
import { CycleReportPage } from './CycleReportPage';
import { PrioritySetGovernance } from './cycles/PrioritySetGovernance';
import { TrackList } from './cycles/TrackList';
import { TrackDetail } from './tracks/TrackDetail';
import { TrackResultPage } from './tracks/TrackResultPage';
import { TrackWorkspace } from './tracks/TrackWorkspace';
import { ReportsIndex } from './reports/ReportsIndex';
import { CycleReports } from './reports/CycleReports';
import { SessionReports } from './reports/SessionReports';
import { SessionResultsReport } from './reports/SessionResultsReport';
import { LoopRollUpReport } from './reports/LoopRollUpReport';
import { PrioritySetReport } from './reports/PrioritySetReport';
import AdvancedSearch from './AdvancedSearch';
import { PipelineHealthReport, PipelineHealthPrintPage } from './reports/PipelineHealthReport';
import ItemTypes from './ItemTypes';
import ItemReadiness from './ItemReadiness';
import { WhiteboardBoardsPage } from './WhiteboardBoardsPage';
import { CycleExecutionReport, CycleExecutionPrintPage } from './reports/CycleExecutionReport';
import { CustomReportPage, CustomReportPrintPage } from './reports/CustomReportPage';
import { ReportSnapshotPage } from './reports/ReportSnapshotPage';
import CustomReportsLibraryPage from './reports/CustomReportsLibraryPage';
import { FeatureFlags } from './settings/FeatureFlags';
import SettingsIndex from './settings/SettingsIndex';
import ItemSources from './settings/ItemSources';
import ItemCategories from './settings/ItemCategories';
import Modules from './settings/Modules';
import WorkspaceSettingsTransfer from './settings/WorkspaceSettingsTransfer';
import ReportSettings from './settings/ReportSettings';
import PlanOfRecordSettings from './settings/PlanOfRecordSettings';
import MyProfile from './settings/MyProfile';
import MyConnectors from './settings/MyConnectors';
import ChangePassword from './settings/ChangePassword';
import ConfigurationsSettings from './settings/ConfigurationsSettings';
import WorkTypesSettings from './settings/WorkTypesSettings';
import LaunchTypesSettings, { InvestmentTypesSettings } from './settings/ItemTaxonomySettings';
import ResourceTypesSettings from './settings/ResourceTypesSettings';
import PlanningDefaultsSettings from './settings/PlanningDefaultsSettings';
import LaunchProfilesSettings from './settings/LaunchProfilesSettings';
import TypePatternsSettings from './settings/TypePatternsSettings';
import PredictabilityProfilesSettings from './settings/PredictabilityProfilesSettings';
import SettingsGroupPage from './settings/SettingsGroupPage';
import SettingsRouteGuard from './settings/SettingsRouteGuard';
// Tournaments
import { TournamentsList } from './tournaments/TournamentsList';
import { TournamentBuilder } from './tournaments/TournamentBuilder';
import { TournamentDetail } from './tournaments/TournamentDetail';

// Other pages
import SystemFlows from './SystemFlows';
import { DocumentationViewer } from '../../components/prioritization/DocumentationViewer';
// New documentation system
import DocHub from './documentation/DocHub';
import ItemManagementDocs from './documentation/areas/ItemManagementDocs';
import CyclesDocs from './documentation/areas/CyclesDocs';
import SessionsDocs from './documentation/areas/SessionsDocs';
import TracksDocs from './documentation/areas/TracksDocs';
import CatchballDocs from './documentation/areas/CatchballDocs';
import ApiReferenceDocs from './documentation/areas/ApiReferenceDocs';
import AutomationsDocs from './documentation/areas/AutomationsDocs';

// Legacy session pages kept for reference (not currently used in v2.2 routes)
// import SessionList from '../sessions/SessionList';
// import SessionPrioritize from '../sessions/SessionPrioritize';

// New session management pages
import { SessionDetail } from './sessions';
import SessionResults from './sessions/SessionResults';
import ReportArtifactPage from './sessions/ReportArtifactPage';
import TVModePresentation from './sessions/TVModePresentation';
import LiveSessionTV from './sessions/LiveSessionTV';
import LiveSessionPage from './sessions/LiveSessionPage';
import GuestRoomScreen from '../../apps/session/GuestRoomScreen';
import { guestPassForPath } from '../../lib/guestParticipant';
import SessionRoomPage from '../session/SessionRoomPage';
import { SessionParticipantFocus } from '../../components/prioritization/session/SessionParticipantFocus';
import Scoreboard from './Scoreboard';
// Event Designer (S1–S5) — the session-design surface at /sessions/new,
// unconditionally (DEP-016 completed 2026-08-31). SESSION_ENGINE_V3 gates only
// the extra /explore route.
import { SessionEngineV3App } from '../../features/session-engine-v3';

// Top-level Tracks and Sessions pages
import TracksPage from '../processes/TracksPage';

// Scoring pages
import { ScoringDashboard, ItemScoringDetail } from './scoring';
import { ReadinessPage } from './ReadinessPage';
import { ReadinessDefinitions } from './settings/ReadinessDefinitions';
import { InputRoundsManager, InputRoundDetail, InputRoundSubmit } from './input-rounds';

// Leveling pages
import { LevelingDashboard, LevelingGrid } from './leveling';
import { ConstraintPanel } from '../../components/prioritization/leveling';
import { TrackCellItemsPage } from './TrackCellItemsPage';

// New Workstream Pages (Feature-flagged)
// WS2: Evidence System
import { EvidenceQuality } from './monitor/EvidenceQuality';
// WS3: Triage System
import { TriageQueue } from './intake/TriageQueue';
// WS4: Cycle Dashboard
import { CycleWorkspaceRedirect } from './catchball/CycleWorkspaceRedirect';
import { ModuleRouteGuard } from '../../components/guards/ModuleRouteGuard';
// WS5: Strategy
import { StrategyList } from './strategy/StrategyList';
import { StrategyDetail } from './strategy/StrategyDetail';
import { KernelList } from './strategy/kernel/KernelList';
import { StrategiesHub } from './strategy/StrategiesHub';
import StrategyObjectsGuidePage from './strategy/StrategyObjectsGuidePage';
import { KernelDetail } from './strategy/kernel/KernelDetail';
import { OSTQuickCreate } from './strategy/OSTQuickCreate';
import { StrategyCanvasList } from './strategy/StrategyCanvasList';
import { StrategyCanvasEditor } from './strategy/StrategyCanvasEditor';
import { V2momList } from './strategy/v2mom/V2momList';
import { V2momEditor } from './strategy/v2mom/V2momEditor';
// Roadmaps (moved from Planning shell so they stay in Prioritization chrome)
import RoadmapList from '../roadmap/RoadmapList';
import RoadmapEditor from '../roadmap/RoadmapEditor';
import { RoadmapPrintPage } from '../roadmap/RoadmapPrintPage';

// Product Roadmap (Now / Next / Later)
import ProductRoadmapEditor from '../product-roadmap/ProductRoadmapEditor';

// OKRs (Objectives & Key Results)
import OKRsPage from './OKRsPage';

// WS6: Catchball
import { CatchballDashboard } from './catchball/CatchballDashboard';

// Legacy workspace/admin pages
import AdminDashboard from './AdminDashboard';
import { CalibrationDashboard } from './CalibrationDashboard';
import AdminPlanningObjects from './admin/AdminPlanningObjects';
import { Realignment } from '../../components/prioritization/spaces/Realignment';

// Quick Exercises
import ExerciseList from './exercises/ExerciseList';
import ExerciseWorkspace from './exercises/ExerciseWorkspace';

// Lexicon — Language Intelligence (experimental)
import LexiconPage from './lexicon/LexiconPage';
import LexiconTranscriptNew from './lexicon/LexiconTranscriptNew';
import LexiconSpeakerProfile from './lexicon/LexiconSpeakerProfile';

// Tools - From Managing Priorities book (17 tools)
import AggregationDemoPage from './tools/AggregationDemoPage';
import ToolsIndex from './Tools';
// Full tool implementations
import CustomMatrix from '../custom-matrix/CustomMatrix';
import EisenhowerMatrix from '../eisenhower-matrix/EisenhowerMatrix';
import RiceScoring from '../rice-scoring/RiceScoring';
import MoscowMethod from '../moscow-method/MoscowMethod';
import WeightedSmallestJobFirst from '../wsjf/WeightedSmallestJobFirst';
import ImpactEffortMatrix from '../impact-effort-matrix/ImpactEffortMatrix';
import StackRanking from '../stack-ranking/StackRanking';
import PairwiseRanking from '../pairwise-ranking/PairwiseRanking';
import DotVoting from '../dot-voting/DotVoting';
import BuyAFeature from '../buy-a-feature/BuyAFeature';
import ParetoAnalysis from '../pareto-analysis/ParetoAnalysis';
import CostOfDelay from '../cost-of-delay/CostOfDelay';
import SpeedBoat from '../speed-boat/SpeedBoat';
import PruneProductTree from '../prune-product-tree/PruneProductTree';
import SimpleWeightedScoring from '../simple-weighted-scoring/SimpleWeightedScoring';
import MaxPrioritiesPyramid from '../max-priorities-pyramid/MaxPrioritiesPyramid';
import FastVsRight from '../fast-vs-right/FastVsRight';
import RelationshipScenarioPage from './tools/RelationshipScenarioPage';
import MagicPrioritization from '../magic-prioritization/MagicPrioritization';
import AppsHome from '@/apps/AppsHome';

// ============================================================================
// Design-First Feature Flag Gate Components
// These swap the traditional React pages for the Peak Priorities canvas
// prototypes when the corresponding feature flag is toggled on.
// ============================================================================


/** Stable empty array for Zustand selectors — never inline `[]` in getSnapshot. */
const EMPTY_CYCLE_LOOPS: CatchballLoop[] = [];

function CycleDetailOrPrototype() {
  const { cycleId } = useParams<{ cycleId: string }>();
  const [searchParams] = useSearchParams();
  const userId = useUserStore((s) => s.userId);
  const cycle = useDegapProcessStore((s) => (cycleId ? s.getCycle(cycleId) : undefined));
  const cycleLoops = useCatchballLoopStore(
    useShallow((s) =>
      cycleId ? s.loops.filter((l) => l.cycleId === cycleId) : EMPTY_CYCLE_LOOPS,
    ),
  );
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const modulesReady = useWorkspaceModulesReady();
  const designerEnabled = useCycleDesignerEnabled();
  const cycleRoleContext = useUserCycleRoleContext({ cycleId, activeLoopId: null });
  const advancedMode = searchParams.get('advanced') === '1';
  const canConfigure =
    cycleRoleContext.isCycleOwner ||
    cycleRoleContext.isAdmin ||
    (workspaceId ? canSync('process.edit', { kind: 'resource', id: workspaceId }) : false);

  if (!modulesReady) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-[var(--text-ter)]">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading cycle…
      </div>
    );
  }

  if (
    cycleId &&
    cycle &&
    designerEnabled &&
    shouldUseCycleDesigner({
      cycle,
      userId,
      isCycleOwner: cycleRoleContext.isCycleOwner,
      isAdmin: canConfigure,
      advancedMode,
      designerEnabled,
    })
  ) {
    return <Navigate to={`/prioritization/cycles/${cycleId}/designer`} replace />;
  }

  if (
    cycleId &&
    cycle &&
    !designerEnabled &&
    shouldUseSetupWizard({
      cycle,
      loops: cycleLoops,
      userId,
      isCycleOwner: cycleRoleContext.isCycleOwner,
      isAdmin: canConfigure,
      advancedMode,
    })
  ) {
    return <Navigate to={`/prioritization/cycles/${cycleId}/setup?step=1`} replace />;
  }

  return (
    <>
      {/* Which mode the cycle is in: being prioritized, or in execution under a Plan of Record.
          Breathing room between it and the cycle's title, as on a plan's pages (PlanDetailLayout). */}
      {cycleId && (
        <div className="mb-6 empty:hidden">
          <RankModeBand cycleId={cycleId} />
          <CycleVersionFlags cycleId={cycleId} className="mt-3" />
        </div>
      )}
      <CycleDetail />
    </>
  );
}

/** Redirects to Cycle Designer when v2 is enabled; otherwise renders the legacy setup wizard. */
function CycleSetupOrDesigner() {
  const { cycleId } = useParams<{ cycleId: string }>();
  const [searchParams] = useSearchParams();
  const userId = useUserStore((s) => s.userId);
  const cycle = useDegapProcessStore((s) => (cycleId ? s.getCycle(cycleId) : undefined));
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const modulesReady = useWorkspaceModulesReady();
  const designerEnabled = useCycleDesignerEnabled();
  const cycleRoleContext = useUserCycleRoleContext({ cycleId, activeLoopId: null });
  const advancedMode = searchParams.get('advanced') === '1';
  const canConfigure =
    cycleRoleContext.isCycleOwner ||
    cycleRoleContext.isAdmin ||
    (workspaceId ? canSync('process.edit', { kind: 'resource', id: workspaceId }) : false);

  if (!modulesReady) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-[var(--text-ter)]">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading cycle setup…
      </div>
    );
  }

  if (!canConfigure) {
    return (
      <Navigate
        to={cycleId ? `/prioritization/cycles/${cycleId}` : '/prioritization/cycles'}
        replace
      />
    );
  }

  if (
    cycleId &&
    cycle &&
    designerEnabled &&
    shouldUseCycleDesigner({
      cycle,
      userId,
      isCycleOwner: cycleRoleContext.isCycleOwner,
      isAdmin: canConfigure,
      advancedMode,
      designerEnabled,
    })
  ) {
    return <Navigate to={`/prioritization/cycles/${cycleId}/designer`} replace />;
  }

  return <CycleSetupWizard />;
}

/**
 * §2.7 gate for the decision-latency report. Waits for modules to resolve
 * before deciding — gating on a not-yet-loaded module reads as "off" and would
 * bounce a permitted user off their own route on every cold load.
 *
 * An admin who reaches this route with the module off is TOLD so (the module
 * is theirs to turn on, and a silent bounce to the dashboard reads as a broken
 * page — see ModuleOffNotice). Everyone else keeps the redirect: the gate
 * behaviour is unchanged, only its legibility is.
 */
function DecisionLatencyRoute() {
  const modulesReady = useWorkspaceModulesReady();
  const enabled = useDecisionLatencyEnabled();
  const currentRole = useUserStore((s) => s.currentRole);

  if (!modulesReady) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-[var(--text-ter)]">
        Loading…
      </div>
    );
  }
  if (!enabled) {
    if (currentRole && isAdminRole(currentRole)) {
      return (
        <ModuleOffNotice
          moduleName="Decision latency report"
          whatItDoes="It decomposes decision time into named stages — framing, deliberation, proposal, approval queue, resolution, ratification, authorization — from timestamps the record already carries."
        />
      );
    }
    return <Navigate to="/prioritization" replace />;
  }
  return <DecisionLatencyReportPage />;
}

function TrackDetailOrWorkspace() {
  if (useModuleGate('track_workspace_v2')) {
    return <TrackWorkspace />;
  }
  return <TrackDetail />;
}

/** §2.6: deep links to the old ungated V2 URL land on the canonical route. */
function TrackWorkspaceBackdoorRedirect() {
  const { trackId } = useParams<{ trackId: string }>();
  return <Navigate to={`/prioritization/tracks/${trackId}`} replace />;
}

/** DEP-011 (V-13): folded cycle sub-views land on the canonical cycle page. */
function CycleIdRedirect() {
  const { cycleId } = useParams<{ cycleId: string }>();
  return <Navigate to={`/prioritization/cycles/${cycleId}`} replace />;
}

/** DEP-009: old catchball v2 wrapper URLs → canonical routes with ?loopId=. */
function CatchballV2TrackRedirect() {
  const { loopId, trackId } = useParams<{ loopId: string; trackId: string }>();
  return <Navigate to={`/prioritization/tracks/${trackId}?loopId=${loopId}`} replace />;
}

function CatchballV2SessionRedirect() {
  const { loopId, sessionId } = useParams<{ loopId: string; sessionId: string }>();
  const [searchParams] = useSearchParams();
  const params = new URLSearchParams(searchParams);
  if (loopId) params.set('loopId', loopId);
  return <Navigate to={`/prioritization/sessions/${sessionId}?${params.toString()}`} replace />;
}

function SessionsListOrPrototype() {
  // The `?view=admin` legacy table was retired 2026-08-27 (DEP-027) — it was a
  // third listing of the same sessions with no rule for preferring it. Any old
  // link now lands on the Session List rather than 404ing.
  return <SessionsSurface />;
}

/**
 * The Event Designer on its secondary /explore route. The Designer itself is
 * the default surface at /sessions/new; this flag only decides whether the
 * additional /explore URL resolves or redirects to the sessions list.
 */
function SessionEngineV3OrRedirect() {
  // Reactive: re-renders when the user's DB-backed flag overrides finish loading.
  const enabled = useFeatureFlag(FEATURE_FLAGS.SESSION_ENGINE_V3);
  // Avoid redirecting before overrides load — the default is off, so a premature
  // read would always bounce the user even when they've enabled the flag.
  if (!areUserFlagsLoaded()) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-granite-gray-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!enabled) {
    return <Navigate to="/prioritization/sessions" replace />;
  }
  return <SessionEngineV3App />;
}



/** Blocks all /catchball/* routes when the Catchball module is disabled. */
function CatchballModuleGuard() {
  const workspaceFeatures = useWorkspaceModules();
  if (!isModuleEnabled(workspaceFeatures, 'catchball')) {
    return <Navigate to="/prioritization" replace />;
  }
  return <Outlet />;
}

// ============================================================================
// Main App Router
// ============================================================================

function PrioritizationAppContent() {
  const location = useLocation();
  const workspaceFeatures = useWorkspaceModules();
  const userId = useUserStore((s) => s.userId);

  // The room's own address, opened by a guest who joined by link and is not
  // signed in (roadmap S-8, ruling R1): they answer here, on the guest screen,
  // through the token-scoped guest functions. Signed in, the address opens the
  // member's room below as before.
  const guestPass = !userId || userId === 'unknown' ? guestPassForPath(location.pathname) : null;
  if (guestPass) {
    return (
      <ToastProvider>
        <GuestRoomScreen pass={guestPass} />
      </ToastProvider>
    );
  }
  
  // Handle demo mode entry point separately (no layout wrapper)
  if (location.pathname === '/prioritization/demo') {
    return <DemoMode />;
  }

  // TV-mode: full-screen routes (no layout wrapper). Layout is what provides the
  // ToastProvider everywhere else under /prioritization, so a shell-free branch
  // has to bring its own or the first child that calls useToast throws and takes
  // the page down (see the session room below for the case that was reproduced).
  const tvModeMatch = location.pathname.match(/^\/prioritization\/sessions\/([^/]+)\/(present|tv)$/);
  if (tvModeMatch) {
    return (
      <ToastProvider>
        <Routes>
          <Route path="sessions/:sessionId/present" element={<TVModePresentation />} />
          <Route path="sessions/:sessionId/tv" element={<LiveSessionTV />} />
        </Routes>
      </ToastProvider>
    );
  }

  // B2C-safe live session room — no nav chrome, no admin tabs.
  // Wrapped in ToastProvider: the participant flow's "Leave session" button
  // (ReverseActionButton) calls useToast, and with no Layout around this branch
  // opening a set-up session from the sessions list crashed with
  // "useToast must be used within a ToastProvider" (reproduced 2026-10-15).
  const sessionRoomMatch = location.pathname.match(/^\/prioritization\/session\/([^/]+)$/);
  if (sessionRoomMatch) {
    return (
      <ToastProvider>
        <Routes>
          <Route path="session/:sessionId" element={<SessionRoomPage />} />
        </Routes>
      </ToastProvider>
    );
  }

  // Item print preview: dedicated surface without nav chrome (@page sizing + clean print preview)
  const itemPrintMatch = location.pathname.match(/^\/prioritization\/items\/([^/]+)\/print$/);
  if (itemPrintMatch) {
    return (
      <Routes>
        <Route path="items/:itemId/print" element={<ItemPrintPage />} />
      </Routes>
    );
  }

  const strategyCanvasPrintMatch = location.pathname.match(
    /^\/prioritization\/strategy\/canvas\/([^/]+)\/print$/,
  );
  if (strategyCanvasPrintMatch) {
    return (
      <Routes>
        <Route path="strategy/canvas/:canvasId/print" element={<StrategyCanvasPrintPage />} />
      </Routes>
    );
  }

  // Prioritization memo print: same dedicated surface without nav chrome
  const memoPrintMatch = /^\/prioritization\/sessions\/[^/]+\/reports\/[^/]+\/print\/?$/.test(location.pathname);
  if (memoPrintMatch) {
    return (
      <Routes>
        <Route path="sessions/:sessionId/reports/:artifactId/print" element={<MemoPrintPage />} />
      </Routes>
    );
  }

  const v2momPrintMatch = /^\/prioritization\/strategy\/v2mom\/[^/]+\/print\/?$/.test(location.pathname);
  if (v2momPrintMatch) {
    return (
      <Routes>
        <Route path="strategy/v2mom/:v2momId/print" element={<V2momPrintPage />} />
      </Routes>
    );
  }

  const roadmapPrintMatch = /^\/prioritization\/roadmap\/[^/]+\/print\/?$/.test(location.pathname);
  if (roadmapPrintMatch) {
    return (
      <Routes>
        <Route path="roadmap/:id/print" element={<RoadmapPrintPage />} />
      </Routes>
    );
  }

  return (
    <PrioritizationLayout>
      <Routes>
        {/* ======================================== */}
        {/* HOME - "What are you prioritizing?"   */}
        {/* ======================================== */}
        <Route index element={<ContributorHomeRedirect />} />
        <Route path="dashboard" element={<ContributorAwareDashboard />} />
        {/* The Apps DIRECTORY keeps the platform nav — it is reached from the
            left nav, and dropping the chrome on click strands you outside the
            product. Individual apps (/apps/<name>) still render their own
            consumer shell; only this index lives in here. */}
        <Route path="apps" element={<AppsHome />} />
        
        {/* ======================================== */}
        {/* INTAKE - Idea submission                */}
        {/* ======================================== */}
        <Route path="intake">
          <Route index element={<Navigate to="queue" replace />} />
          <Route path="submit" element={<SubmitIdea />} />
          <Route path="embed" element={
            <ModuleRouteGuard moduleKey="intake_embed" redirectTo="/prioritization/intake/submit">
              <IntakeEmbedPage />
            </ModuleRouteGuard>
          } />
          <Route path="mine" element={<MySubmissions />} />
          <Route path="queue" element={<IntakeQueue />} />
          {/* WS3: Triage Queue */}
          <Route path="triage" element={<TriageQueue />} />
        </Route>

        {/* ======================================== */}
        {/* STAKEHOLDER PULSE                        */}
        {/* ======================================== */}
        <Route path="pulse" element={<StakeholderPulsePage />} />
        
        {/* ======================================== */}
        {/* SHAPE - Readiness Queue & Structure     */}
        {/* ======================================== */}
        <Route path="shape">
          <Route index element={<Navigate to="queue" replace />} />
          <Route path="queue" element={<ItemShaping />} />
          <Route path="attributes" element={<Navigate to="/prioritization/settings/attributes" replace />} />
          <Route path="items" element={<Navigate to="/prioritization/items" replace />} />
          <Route path="clusters" element={<Navigate to="/prioritization/relationships/clusters" replace />} />
          <Route path="hierarchy" element={<ItemHierarchy />} />
          <Route path="relationships" element={<RelationshipGraph />} />
        </Route>
        
        {/* ======================================== */}
        {/* SCORING - Item attribute value entry    */}
        {/* ======================================== */}
        <Route path="scoring">
          <Route index element={<ScoringDashboard />} />
          <Route path=":itemId" element={<ItemScoringDetail />} />
        </Route>

        {/* ======================================== */}
        {/* READINESS - Context-aware item readiness */}
        {/* ======================================== */}
        <Route path="readiness" element={<ReadinessPage />} />
        
        {/* ======================================== */}
        {/* INPUT ROUNDS - Crowd input collection   */}
        {/* Module `input_rounds` — off until a workspace admin switches it on (owner, 2026-10-16). */}
        {/* ======================================== */}
        <Route path="input-rounds" element={<ModuleRouteGuard moduleKey="input_rounds" />}>
          <Route index element={<InputRoundsManager />} />
          <Route path=":roundId" element={<InputRoundDetail />} />
          <Route path=":roundId/submit" element={<InputRoundSubmit />} />
        </Route>

        {/* ======================================== */}
        {/* LEVELING - Altitude alignment            */}
        {/* ======================================== */}
        <Route path="leveling">
          <Route index element={<LevelingDashboard />} />
          <Route path="grid" element={<LevelingGrid />} />
          <Route path="constraints" element={<ConstraintPanel />} />
        </Route>
        
        {/* ======================================== */}
        {/* ITEMS - Master-Detail View + Item Detail */}
        {/* ======================================== */}
        <Route path="items" element={<ItemListView />} />
        <Route path="items/triage" element={<ItemListView viewMode="triage" />} />
        <Route path="items/needs-definition" element={<ItemListView viewMode="needs-shaping" />} />
        {/* Legacy redirects */}
        <Route path="items/missing-metadata" element={<Navigate to="/prioritization/items/needs-definition" replace />} />
        <Route path="items/needs-defining" element={<Navigate to="/prioritization/items/needs-definition" replace />} />
        <Route path="items/types" element={<ItemTypes />} />
        <Route path="items/whiteboards" element={<WhiteboardBoardsPage />} />
        <Route path="items/readiness" element={<ItemReadiness />} />
        {/* DEP-006 (2026-07-07): legacy Items page deleted — ItemListView at /items is canonical */}
        <Route path="items-legacy" element={<Navigate to="/prioritization/items" replace />} />
        <Route path="items/track-cell/:category/:level" element={<TrackCellItemsPage />} />
        <Route path="items/:itemId/journey" element={<ItemJourneyPage />} />
        <Route path="items/:itemId" element={<ItemDetailPage />} />
        <Route path="workspace-journey" element={<WorkspaceJourneyPage />} />
        {isModuleFeatureAccessible(workspaceFeatures, 'delivery_tracking', FEATURE_FLAGS.DELIVERY_TRACKING) && isModuleEnabled(workspaceFeatures, 'decision_calibration') && (
          <Route path="calibration" element={<CalibrationDashboard />} />
        )}
        {/* Legacy: deep-link compatibility */}
        <Route path="candidates" element={<Navigate to="/prioritization/items" replace />} />

        {/* ======================================== */}
        {/* RELATIONSHIPS - Filtered item views      */}
        {/* ======================================== */}
        <Route path="relationships">
          <Route index element={<RelationshipsHomePage />} />
          <Route path="clusters" element={<RelationshipItemsPage relationshipType="cluster" />} />
          <Route path="clusters/:focusGroupId" element={<RelationshipItemsPage relationshipType="cluster" />} />
          <Route path="packages" element={<RelationshipItemsPage relationshipType="package" />} />
          <Route path="packages/:focusGroupId" element={<RelationshipItemsPage relationshipType="package" />} />
          {/* Legacy 'blocs' route — Package was formerly named Bloc. Redirect for back-compat. */}
          <Route path="blocs" element={<Navigate to="/prioritization/relationships/packages" replace />} />
          <Route path="blocs/:focusGroupId" element={<RelationshipItemsPage relationshipType="package" />} />
          <Route path="exclusives" element={<RelationshipItemsPage relationshipType="exclusive" />} />
          <Route path="exclusives/:focusGroupId" element={<RelationshipItemsPage relationshipType="exclusive" />} />
          <Route path="sequence" element={<RelationshipItemsPage relationshipType="sequence" />} />
          <Route path="dependencies" element={<Navigate to="/prioritization/relationships/sequence" replace />} />
          <Route path="prerequisites" element={<Navigate to="/prioritization/relationships/sequence" replace />} />
          <Route path="composition" element={<RelationshipItemsPage relationshipType="composition" />} />
          <Route path="aggregations" element={<Navigate to="/prioritization/relationships/composition" replace />} />
          <Route path="breakdowns" element={<Navigate to="/prioritization/relationships/composition" replace />} />
          <Route path="contains" element={<RelationshipItemsPage relationshipType="contains" />} />
          <Route path="elaborations" element={<RelationshipItemsPage relationshipType="elaboration" />} />
          <Route path="reframes" element={<RelationshipItemsPage relationshipType="reframe" />} />
          <Route path="lineage" element={<RelationshipItemsPage relationshipType="lineage" />} />
        </Route>

        {/* ======================================== */}
        {/* COLLECTIONS - Thematic grouping          */}
        {/* ======================================== */}
        <Route path="collections" element={<CollectionsPage />} />
        <Route path="collections/:collectionId" element={<CollectionsPage />} />

        {/* ======================================== */}
        {/* COMMITMENT REGISTRY                      */}
        {/* ======================================== */}
        <Route path="commitments" element={
          <ModuleRouteGuard moduleKey="commitment_registry">
            <CommitmentRegistryPage />
          </ModuleRouteGuard>
        } />
        <Route path="commitments/:commitmentId" element={
          <ModuleRouteGuard moduleKey="commitment_registry">
            <CommitmentDetailPage />
          </ModuleRouteGuard>
        } />

        {/* ======================================== */}
        {/* CYCLES - Prioritization Cycles          */}
        {/* ======================================== */}
        <Route path="cycles">
          <Route index element={<ProcessList />} />
          {/* V-13 (DEP-011): compact overview folded into CycleDetail — redirect */}
          <Route path="overview" element={<Navigate to="/prioritization/cycles" replace />} />
          <Route path="new" element={<ProcessList />} />
          {/* Guided setup wizard — cycle creators land here after create until kickoff */}
          <Route path=":cycleId/setup" element={<CycleSetupOrDesigner />} />
          {/* Cycle Designer — visual path builder (cycle_designer_v2) */}
          <Route
            path=":cycleId/designer"
            element={
              // A throw here used to escape to the browser's own error page, which
              // says nothing and implies the design is lost. It isn't — the draft
              // autosaves to cycle metadata.
              <ErrorBoundary componentName="Cycle Designer" fallback={<DesignerErrorFallback />}>
                <CycleDesignerWizard />
              </ErrorBoundary>
            }
          />
          <Route path=":cycleId" element={<CycleDetailOrPrototype />} />
          {/* V-13 (DEP-011): overview + dashboard folded into CycleDetail's Overview tab */}
          <Route path=":cycleId/overview" element={<CycleIdRedirect />} />
          <Route path=":cycleId/dashboard" element={<CycleIdRedirect />} />
          {/* 
            LEGACY: Phase-based routes - v2.3 deprecates cycle-global phase navigation.
            Work now happens at the Track level. Redirect to cycle overview.
          */}
          <Route path=":cycleId/gather" element={<CyclePhaseRedirect />} />
          <Route path=":cycleId/arrange" element={<CyclePhaseRedirect />} />
          <Route path=":cycleId/prioritize" element={<CyclePhaseRedirect />} />
          <Route path=":cycleId/configure" element={<CycleConfig />} />
          <Route path=":cycleId/results" element={<CycleResults />} />
          {/* Cycle Report / Plan of Record — Brief G */}
          <Route path=":cycleId/report" element={<CycleReportPage />} />
          <Route path=":cycleId/governance" element={<PrioritySetGovernance />} />
          {/* The client-side Aggregation Review is gone (roadmap S-13): it combined
              sessions with a Borda sum that never divided. A track's sessions are
              combined on the server (publish_track_result, ruling R7); the cycle's
              results read them. */}
          <Route path=":cycleId/aggregation" element={<RedirectCycleAggregationToResults />} />
          {/* Track list for this cycle (optional alternate to Tracks tab on cycle detail) */}
          <Route path=":cycleId/tracks" element={<TrackList />} />
          {/* Cycle Workspace — redirect to the active loop's workspace */}
          <Route path=":cycleId/workspace" element={<CycleWorkspaceRedirect />} />
        </Route>
        
        {/* ======================================== */}
        {/* CYCLE DESIGN - Configuration workspace  */}
        {/* ======================================== */}
        <Route path="cycle-design" element={<CycleDesign />} />
        <Route path="cycle-design/collections" element={<CycleDesignCollectionsPage />} />

        {/* ======================================== */}
        {/* GOVERNANCE - Proposals & Ratification   */}
        {/* ======================================== */}
        <Route path="governance">
          <Route index element={<Navigate to="proposals" replace />} />
          <Route path="proposals" element={<ProposalsPage />} />
          <Route path="proposals/:proposalId" element={<PriorityProposalReviewPage />} />
          <Route path="ratification" element={<RatificationPage />} />
        </Route>

        {/* ======================================== */}
        {/* TRACKS - Track List & Detail Pages */}
        {/* ======================================== */}
        <Route path="tracks">
          <Route index element={<TracksPage />} />
          {/* TrackDetail is the main track view — swapped for TrackWorkspace v2 when flag is on */}
          <Route path=":trackId" element={<ExchangeContextFrame><TrackDetailOrWorkspace /></ExchangeContextFrame>} />
          {/* The result published for the track (mig 934) — one address Catchball and the Plan of Record can point at. */}
          <Route path=":trackId/result" element={<TrackResultPage />} />
        </Route>

        {/* ======================================== */}
        {/* Track Workspace back door — closed under §2.6 (V-02): one route per
            surface. The V2 workspace is reachable only via the track_workspace_v2
            gate at /prioritization/tracks/:trackId. */}
        {/* ======================================== */}
        <Route path="track-workspace">
          <Route index element={<Navigate to="/prioritization/tracks" replace />} />
          <Route path=":trackId" element={<TrackWorkspaceBackdoorRedirect />} />
        </Route>
        
        {/* ======================================== */}
        {/* CATCHBALL - Bidirectional Priority Flow */}
        {/* ======================================== */}
        {/* Every old catchball URL still resolves — see LegacyCatchballRedirect. */}
        <Route path="catchball/*" element={<LegacyCatchballRedirect />} />

        <Route path="cascade" element={<CatchballModuleGuard />}>
          {/* Legacy /prioritization/catchball/* deep links redirect in from App.tsx. */}
          {/* Cascade landing: cycle list + personal "in your court" highlight */}
          <Route index element={<CatchballExchangeLandingPage />} />
          {/* Per-cycle cascade: every Loop drawn as the Path it runs over */}
          <Route path=":cycleId" element={<CatchballExchangeCyclePage />} />
          {/* Role-aware single-loop exchange view → Catchball (DEP-025) */}
          <Route path=":cycleId/loop/:loopId" element={<CatchballExchangeLoopToDeskRedirect />} />
          {/* Catchball — the holder surface: claim, fork, attach sessions, pass on. */}
          <Route path="catchball/:cycleId/:loopId" element={<CatchballInboxPage />} />
          {/* Canonical dashboard URL — static paths must come before :cycleId catch-all */}
          <Route path="dashboard/:cycleId" element={<CatchballDashboard />} />
          {/* Legacy aliases → redirect to canonical dashboard URL */}
          <Route path="cycle/:cycleId" element={<CatchballDashboardRedirect />} />
          <Route path="compose/:cycleId" element={<RequestComposerPage />} />
          {/* Retired 2026-08-28: both surfaces were reachable only from links inside
              themselves, and every capability they had lives elsewhere (createItem has
              20+ homes; the intelligence page made no state changes at all). Redirected
              rather than removed so any bookmarked deep link still lands somewhere. */}
          <Route path="contribute/:cycleId" element={<RetiredToCycleExchange />} />
          <Route path="intelligence/:cycleId" element={<RetiredToCycleExchange />} />
          <Route path="reconcile/:cycleId" element={<ExecutiveReconciliationPage />} />
          <Route path="setup/:cycleId" element={<CatchballSetupPage />} />
          <Route path="ball/:cycleId/:loopId/:ballId" element={<BallDetailToDeskRedirect />} />
          <Route path="inbox/:cycleId" element={<ObjectiveInboxWithParams />} />
          <Route path="executive-inbox" element={<ExecutiveInboxPage />} />
          <Route path="proposal/:proposalId" element={<ProposalReviewPage />} />
          {/* V-09 (DEP-009): the v2 wrapper family is retired — ball context now
              rides canonical routes as ?loopId= (ExchangeContextFrame). Redirects
              preserve old deep links. */}
          <Route path="v2/loop/:loopId/track/:trackId" element={<CatchballV2TrackRedirect />} />
          <Route path="v2/loop/:loopId/session/:sessionId" element={<CatchballV2SessionRedirect />} />
        </Route>
        
        {/* ======================================== */}
        {/* MY TASKS - Participant Task View        */}
        {/* ======================================== */}
        <Route path="my-work" element={<MyWorkPage />} />
        {/* The Tasks page became My work (PRD-tasks-asks-and-my-work FR-3). */}
        <Route path="my-tasks" element={<Navigate to="/prioritization/my-work" replace />} />
        <Route path="task/:taskId" element={<TaskRunner />} />
        
        {/* ======================================== */}
        {/* NOTIFICATIONS - Notification Center     */}
        {/* ======================================== */}
        <Route path="notifications" element={<NotificationCenter />} />
        
        {/* ======================================== */}
        {/* SESSIONS - Prioritization Sessions      */}
        {/* ======================================== */}
        <Route path="sessions" element={<Outlet />}>
          {/* Sessions list — swapped for Sessions Redesign v2 canvas when flag is on */}
          <Route index element={<SessionsListOrPrototype />} />
          {/* Event Designer on its secondary /explore route (flag SESSION_ENGINE_V3).
              The primary route is /sessions/new — see SessionsNewRoute below. */}
          {/* ExchangeContextFrame: when ?loopId= is present, show ← Back to Catchball */}
          <Route path="explore" element={<ExchangeContextFrame><SessionEngineV3OrRedirect /></ExchangeContextFrame>} />
          {/* ExchangeContextFrame: Catchball → Configure session arrives with ?loopId= */}
          <Route path="new" element={<ExchangeContextFrame><SessionEngineV3App /></ExchangeContextFrame>} />
          <Route path="list" element={<Navigate to="/prioritization/sessions" replace />} />
          <Route path=":id/results" element={<ExchangeContextFrame><SessionResults /></ExchangeContextFrame>} />
          <Route path=":sessionId/reports/:artifactId" element={<ReportArtifactPage />} />
          {/* TV-mode is handled above outside the layout — these are safety fallbacks */}
          <Route path=":sessionId/present" element={<TVModePresentation />} />
          <Route path=":sessionId/tv" element={<LiveSessionTV />} />
          <Route path=":sessionId/participate" element={<SessionParticipantFocus />} />
          <Route path=":sessionId/live" element={<ExchangeContextFrame><LiveSessionPage /></ExchangeContextFrame>} />
          <Route path=":sessionId" element={<ExchangeContextFrame><SessionDetail /></ExchangeContextFrame>} />
        </Route>
        
        {/* ======================================== */}
        {/* FOCUS LISTS                             */}
        {/* ======================================== */}
        <Route path="lists">
          <Route index element={<ItemPoolEditor />} />
          <Route path=":listId" element={<ItemPoolEditor />} />
          <Route path=":listId/edit" element={<ItemPoolEditor />} />
        </Route>
        
        {/* ======================================== */}
        {/* SCOREBOARD - Cross-session live rankings */}
        {/* ======================================== */}
        <Route path="scoreboard" element={<Scoreboard />} />

        {/* ======================================== */}
        {/* PRIORITIES — retired 2026-10-15: it was a second Item Pools page.  */}
        {/* Item pools live at /prioritization/lists; a cycle's ranking at    */}
        {/* /prioritization/cycles/:id/results.                               */}
        {/* ======================================== */}
        <Route path="priorities" element={<Navigate to="/prioritization/lists" replace />} />
        {/* DEP-020 / DEP-021: plan cycles + Plan of Record home live under Planning */}
        <Route path="plans" element={<Navigate to="/planning/plans" replace />} />
        <Route path="plans/:planCycleId" element={<RedirectDeprecatedPrioritizationPlanCycle />} />
        <Route path="plan-of-record" element={<Navigate to="/planning/plans" replace />} />
        <Route path="plan-of-record/*" element={<Navigate to="/planning/plans" replace />} />
        {/* Legacy cycle-report handoff URL (never existed as a page) */}
        <Route path="planning" element={<Navigate to="/planning/plans?catalog=1" replace />} />
        
        {/* ======================================== */}
        {/* TOOLS - Prioritization Methods          */}
        {/* ======================================== */}
        {/* EXERCISES - Quick Exercises             */}
        {/* ======================================== */}
        <Route path="exercises">
          <Route index element={<ExerciseList />} />
          <Route path=":exerciseId" element={<ExerciseWorkspace />} />
        </Route>

        {/* Aggregation Demo (must precede the tools nested routes) */}
        <Route path="tools/aggregation-demo" element={<AggregationDemoPage />} />

        {/* From Managing Priorities book (17 tools)*/}
        {/* ======================================== */}
        <Route path="tools">
          <Route index element={<ToolsIndex />} />
          <Route path="custom-matrix" element={<CustomMatrix />} />
          <Route path="custom-matrix/:templateId" element={<CustomMatrix />} />
          <Route path="eisenhower-matrix" element={<EisenhowerMatrix />} />
          <Route path="rice-scoring" element={<RiceScoring />} />
          <Route path="moscow-method" element={<MoscowMethod />} />
          <Route path="wsjf" element={<WeightedSmallestJobFirst />} />
          <Route path="impact-effort-matrix" element={<ImpactEffortMatrix />} />
          <Route path="stack-ranking" element={<StackRanking />} />
          <Route path="pairwise-ranking" element={<PairwiseRanking />} />
          <Route path="dot-voting" element={<DotVoting />} />
          <Route path="buy-a-feature" element={<BuyAFeature />} />
          <Route path="pareto-analysis" element={<ParetoAnalysis />} />
          <Route path="cost-of-delay" element={<CostOfDelay />} />
          <Route path="speed-boat" element={<SpeedBoat />} />
          <Route path="prune-product-tree" element={<PruneProductTree />} />
          <Route path="simple-weighted-scoring" element={<SimpleWeightedScoring />} />
          <Route path="max-priorities-pyramid" element={<MaxPrioritiesPyramid />} />
          <Route path="fast-vs-right" element={<FastVsRight />} />
          <Route path="ahp" element={<Navigate to="/prioritization/tools" replace />} />
          <Route path="relationship-scenario-planner" element={<RelationshipScenarioPage />} />
          <Route path="magic-prioritization" element={<MagicPrioritization />} />
        </Route>
        
        {/* ======================================== */}
        {/* MONITOR - Drift, Map, Analytics         */}
        {/* ======================================== */}
        <Route path="monitor">
          <Route index element={<Navigate to="drift" replace />} />
          <Route path="drift" element={<DriftDetection />} />
          <Route path="realignment" element={<Realignment />} />
          <Route path="map" element={<FocusMap />} />
          <Route path="analytics" element={<Analytics />} />
          {/* WS2: Evidence Quality Dashboard */}
          <Route path="evidence" element={
            <ModuleRouteGuard moduleKey="evidence">
              <EvidenceQuality />
            </ModuleRouteGuard>
          } />
        </Route>
        
        {/* ======================================== */}
        {/* STRATEGY - Alignment, Decisions, Discovery */}
        {/* ======================================== */}
        {/* The section is the `strategy` module; Kernel, Canvas and V2MOM are modules of
            their own. The sidebar hides their entries when a workspace switches one off —
            the address leaves the same way, through the one module route guard (§2.7). */}
        <Route path="strategy" element={<ModuleRouteGuard moduleKey="strategy" />}>
          <Route index element={<StrategiesHub />} />
          <Route path="guided-setup" element={<StrategyObjectsGuidePage />} />
          <Route path="objectives" element={<ObjectivesPage />} />
          <Route path="goals" element={<GoalsPage />} />
          <Route path="okrs" element={<OKRsPage />} />
          <Route path="alignment" element={<StrategyAlignment />} />
          <Route path="decisions" element={<DecisionHistory />} />
          <Route path="opportunity-trees" element={<Navigate to="/prioritization/strategy" replace />} />
          <Route path="opportunity-trees/new" element={<OSTQuickCreate />} />
          <Route path="opportunity-tree" element={<Navigate to="/prioritization/strategy" replace />} />
          <Route path="opportunity-tree/:treeId" element={<OpportunitySolutionTree />} />
          {/* WS5: Strategy Chain Pages */}
          <Route path="list" element={<StrategyList />} />
          <Route path=":strategyId" element={<StrategyDetail />} />
          {/* Kernel Strategy Module (Rumelt) */}
          <Route path="kernel" element={<ModuleRouteGuard moduleKey="strategy_kernel" redirectTo="/prioritization/strategy" />}>
            <Route index element={<KernelList />} />
            <Route path=":kernelId" element={<KernelDetail />} />
          </Route>
          {/* Strategy Canvas */}
          <Route path="canvas" element={<ModuleRouteGuard moduleKey="strategy_canvas" redirectTo="/prioritization/strategy" />}>
            <Route index element={<StrategyCanvasList />} />
            <Route path=":canvasId" element={<StrategyCanvasEditor />} />
          </Route>

          {/* V2MOM */}
          <Route path="v2mom" element={<ModuleRouteGuard moduleKey="v2mom" redirectTo="/prioritization/strategy" />}>
            <Route index element={<V2momList />} />
            <Route path=":v2momId" element={<V2momEditor />} />
          </Route>
        </Route>

        {/* ======================================== */}
        {/* ROADMAPS                                */}
        {/* ======================================== */}
        <Route path="roadmap" element={<RoadmapList />} />
        <Route path="roadmap/:id" element={<RoadmapEditor />} />

        {/* ======================================== */}
        {/* PRODUCT ROADMAP (Now / Next / Later)    */}
        {/* List redirects to unified Roadmap hub;  */}
        {/* individual editors remain at their URL. */}
        {/* ======================================== */}
        <Route path="product-roadmap" element={<Navigate to="/prioritization/roadmap" replace />} />
        <Route path="product-roadmap/:id" element={<ProductRoadmapEditor />} />

        {/* ======================================== */}
        {/* REPORTS                                 */}
        {/* ======================================== */}
        <Route path="reports" element={<ModuleRouteGuard moduleKey="reports" />}>
          <Route index element={<ReportsIndex />} />
          <Route path="cycles" element={<CycleReports />} />
          <Route path="sessions" element={<SessionReports />} />
          {/* Canonical Reporting Surfaces */}
          {/* ExchangeContextFrame: after Close & save, report keeps ?loopId= for ← Catchball */}
          <Route path="session/:sessionId" element={<ExchangeContextFrame><SessionResultsReport /></ExchangeContextFrame>} />
          <Route path="loop/:loopId" element={<LoopRollUpReport />} />
          <Route path="priority-set/:prioritySetId" element={<PrioritySetReport />} />
          <Route path="custom" element={<CustomReportsLibraryPage />} />
          <Route path="custom/new" element={<CustomReportPage />} />
          <Route path="custom/:id" element={<CustomReportPage />} />
          <Route path="relationship-health" element={<RelationshipHealthPage />} />
          <Route path="pipeline-health" element={<PipelineHealthReport />} />
          <Route path="pipeline-health/print" element={<PipelineHealthPrintPage />} />
          {/* The Item Readiness report became Pipeline Health (roadmap §2.1); old links still land. */}
          <Route path="item-readiness" element={<Navigate to="/prioritization/reports/pipeline-health" replace />} />
          <Route path="execution" element={<CycleExecutionReport />} />
          {/* RP-7: every report has a print view (PDF) and a snapshot address; both carry the report's controls in the query. */}
          <Route path="execution/print" element={<CycleExecutionPrintPage />} />
          <Route path="custom/:id/print" element={<CustomReportPrintPage />} />
          <Route path="snapshots/:id" element={<ReportSnapshotPage />} />
          <Route path="snapshots/:id/print" element={<ReportSnapshotPage print />} />
        </Route>

        {/* ======================================== */}
        {/* TOURNAMENTS                             */}
        {/* ======================================== */}
        <Route path="tournaments" element={<ModuleRouteGuard moduleKey="tournaments" />}>
          <Route index element={<TournamentsList />} />
          <Route path="new" element={<TournamentBuilder />} />
          <Route path=":tournamentId" element={<TournamentDetail />} />
        </Route>

        {/* ======================================== */}
        {/* DECISIONS - Decision log                */}
        {/* ======================================== */}
        <Route path="decisions" element={<DecisionsPage />} />
        {/* Decision Log bridge — gated by the decision_brief module (off by
            default); off → the ungated Decisions page. */}
        <Route
          path="decisions/brief"
          element={(
            <ModuleRouteGuard moduleKey="decision_brief" redirectTo="/prioritization/decisions">
              <DecisionBriefPage />
            </ModuleRouteGuard>
          )}
        />
        {/* The canonical URL of one decision: its own page, like an item's. */}
        <Route path="decisions/:decisionId" element={<DecisionDetailPage />} />

        {/* ======================================== */}
        {/* THE GAME — the wristband: the whole trained vocabulary on one page */}
        {/* ======================================== */}
        <Route path="wristband" element={<WristbandPage />} />

        {/* ======================================== */}
        {/* DECISION INTELLIGENCE                    */}
        {/* ======================================== */}
        <Route
          path="decision-intelligence"
          element={isModuleEnabled(workspaceFeatures, 'decision_intelligence') ? <DecisionIntelligence /> : <Navigate to="/prioritization/decisions" replace />}
        />

        {/* ======================================== */}
        {/* DECISION RECORDS — ambient judgment-lane artifacts */}
        {/* ======================================== */}
        <Route path="decision-records" element={<DecisionRecords />} />

        {/* ======================================== */}
        {/* COGNITIVE BIAS AUDIT                     */}
        {/* ======================================== */}
        <Route
          path="insights/cognitive-bias"
          element={isModuleEnabled(workspaceFeatures, 'bias_audit') ? <CognitiveBiasPage /> : <Navigate to="/prioritization/decisions" replace />}
        />

        {/* ======================================== */}
        {/* JUDGMENT INDEX                           */}
        {/* ======================================== */}
        {isModuleFeatureAccessible(workspaceFeatures, 'judgment_index', FEATURE_FLAGS.JUDGMENT_INDEX) && (
          <Route path="insights/judgment-index" element={<JudgmentIndexPage />} />
        )}

        {/* ======================================== */}
        {/* JUDGMENT ANALYTICS                       */}
        {/* ======================================== */}
        {isModuleFeatureAccessible(workspaceFeatures, 'judgment_analytics', FEATURE_FLAGS.JUDGMENT_ANALYTICS) && (
          <Route path="insights/judgment-analytics" element={<JudgmentAnalyticsPage />} />
        )}

        {/* ======================================== */}
        {/* PROVENANCE - System-wide activity feed  */}
        {/* ======================================== */}
        <Route path="lineage" element={<LineagePage />} />

        {/* ======================================== */}
        {/* SEARCH - Advanced faceted search        */}
        {/* ======================================== */}
        <Route path="search" element={<AdvancedSearch />} />
        
        {/* ======================================== */}
        {/* TWINS - Duplicate/variant cluster report */}
        {/* ======================================== */}
        {/* DEP-037: the hand-built Twins page is retired onto the template report page. */}
        <Route path="twins" element={<Navigate to="/prioritization/agents/twins" replace />} />
        <Route path="agents/:agentType" element={<AgentReportPage />} />
        {/* The estimator-only report addresses (before the generic page, spec §5) still open it. */}
        <Route path="value-estimator" element={<LegacyEstimatorReportRedirect />} />
        <Route path="value-estimator/:agentType" element={<LegacyEstimatorReportRedirect />} />

        {/* ======================================== */}
        {/* NEXUS - Relationship candidate review    */}
        {/* ======================================== */}
        {/* DEP-038: the hand-built Nexus page is retired onto the template report page. */}
        <Route path="nexus" element={<Navigate to="/prioritization/agents/nexus" replace />} />

        {/* ======================================== */}
        {/* SCRIBE - Natural language item intake    */}
        {/* ======================================== */}
        <Route path="scribe" element={<ScribePage />} />

        {/* ======================================== */}
        {/* INTELLIGENCE AGENTS - finding reports    */}
        {/* ======================================== */}
        <Route path="findings" element={<AllFindings />} />
        {/* DEP-040: the five finding agents report on the template report page. */}
        <Route path="fracture" element={<Navigate to="/prioritization/agents/cassandra" replace />} />
        {/* The address before the agent was named Fracture; saved links still land. */}
        <Route path="cassandra" element={<Navigate to="/prioritization/agents/cassandra" replace />} />
        <Route path="compass" element={<Navigate to="/prioritization/agents/compass" replace />} />
        <Route path="keystone" element={<Navigate to="/prioritization/agents/keystone" replace />} />
        <Route path="meridian" element={<Navigate to="/prioritization/agents/meridian" replace />} />
        <Route path="condition-watch" element={<Navigate to="/prioritization/agents/condition_watch" replace />} />
        <Route path="beacon" element={<Navigate to="/prioritization/agents/beacon" replace />} />
        <Route path="review-due" element={<Navigate to="/prioritization/agents/review_due" replace />} />

        {/* ======================================== */}
        {/* DECISION LATENCY - stage decomposition   */}
        {/* ======================================== */}
        <Route path="decision-latency" element={<DecisionLatencyRoute />} />

        {/* ======================================== */}
        {/* CLARITY TOOLS                           */}
        {/* ======================================== */}
        <Route path="outcome-drivers" element={<OutcomeDriversPage />} />
        <Route path="outcome-drivers/new" element={<NewOutcomeDriverPage />} />
        <Route path="desired-outcomes" element={<DesiredOutcomesList />} />
        <Route path="desired-outcomes/:id" element={<DesiredOutcomeEditor />} />
        <Route path="open-questions" element={<OpenQuestionsPage />} />
        <Route path="backplan" element={<BackplanList />} />
        <Route path="backplan/:id" element={<BackplanEditor />} />

        {/* ======================================== */}
        {/* SETTINGS                                */}
        {/* ======================================== */}
        <Route path="settings" element={<SettingsRouteGuard />}>
          <Route index element={<SettingsIndex />} />
          {/* My roles: the featured-roles picker (user_rich_profiles.featured_job_role_slugs), read by
              the role-based menu and the home. It was Settings → Home until 2026-10-14; the old
              address still opens it. */}
          <Route path="my-roles" element={<FeaturedRolesSettings />} />
          <Route path="home" element={<Navigate to="/prioritization/settings/my-roles" replace />} />
          <Route path="spaces" element={<SpacesSettings />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="users/:userId/permissions" element={<UserPermissionsReport />} />
          <Route path="invites" element={<AdminInvites />} />
          <Route path="grant-workspace-admin" element={<Navigate to="/prioritization/settings/permissions" replace />} />
          <Route path="groups" element={<AdminGroups />} />
          <Route path="roles" element={<AdminRoles />} />
          <Route path="role-packages" element={<RolePackages />} />
          <Route path="policies" element={<Policies />} />
          <Route path="permissions" element={<Permissions />} />
          <Route path="defaults" element={<Navigate to="/prioritization/settings/workflows" replace />} />
          <Route path="workflows" element={<Workflows />} />
          <Route path="workflows/guided" element={<WorkflowSetupWizard />} />
          <Route path="tracks" element={<TrackSettings />} />
          <Route path="comparison-frames" element={<Navigate to="/prioritization/settings/tracks" replace />} />
          <Route path="templates" element={<Templates />} />
          <Route path="templates/guided" element={<TemplateSetupWizard />} />
          <Route path="org-hierarchy" element={
            <ModuleRouteGuard moduleKey="org_hierarchy" redirectTo="/prioritization/settings">
              <AdminOrgHierarchy />
            </ModuleRouteGuard>
          } />
          <Route path="product-hierarchy" element={
            <ModuleRouteGuard moduleKey="product_hierarchy" featureFlagKey={FEATURE_FLAGS.PRODUCT_HIERARCHY} redirectTo="/prioritization/settings">
              <ProductHierarchy />
            </ModuleRouteGuard>
          } />
          <Route path="identity" element={<AdminIdentity />} />
          <Route path="connectors" element={<Connectors />} />
          <Route path="field-mappings" element={<FieldMappings />} />
          <Route path="csv-citizenship" element={<CsvCitizenshipSettings />} />
          {/* Insights & Accountability */}
          <Route path="accountability" element={<AccountabilityTrail />} />
          <Route path="audit-log" element={<WorkspaceAuditLog />} />
          <Route path="onboarding-funnel" element={<OnboardingFunnel />} />
          <Route path="experience-profiles" element={<ExperienceProfiles />} />
          <Route path="experience-audit" element={<ExperienceAudit />} />
          <Route path="reports" element={<SettingsGroupPage groupKey="reports" />} />
          <Route path="insights" element={<Navigate to="/prioritization/settings/reports" replace />} />
          <Route path="sync-log" element={<SyncEventLog />} />
          <Route path="automations" element={<Automations />} />
          <Route path="ambient-runtime" element={
            <ModuleRouteGuard moduleKey="ambient_runtime" featureFlagKey={FEATURE_FLAGS.AMBIENT_RUNTIME} redirectTo="/prioritization/settings">
              <AmbientRuntime />
            </ModuleRouteGuard>
          } />
          <Route path="excursions" element={
            <ModuleRouteGuard moduleKey="semantic_connectors" featureFlagKey={FEATURE_FLAGS.SEMANTIC_CONNECTORS} redirectTo="/prioritization/settings">
              <Excursions />
            </ModuleRouteGuard>
          } />
          <Route path="conflict-queue" element={<ConflictQueue />} />
          <Route path="sync-health" element={<SyncHealth />} />
          <Route path="entity-linker" element={<EntityLinker />} />
          <Route path="api" element={<ApiKeys />} />
          <Route path="status" element={<WorkspaceStatus />} />
          <Route path="plan" element={<WorkspacePlanPage />} />
          <Route path="attributes" element={
            <ModuleRouteGuard moduleKey="attributes" redirectTo="/prioritization/settings">
              <AttributeManagement />
            </ModuleRouteGuard>
          } />
          <Route path="custom-fields" element={
            <ModuleRouteGuard moduleKey="attributes" redirectTo="/prioritization/settings">
              <CustomFields />
            </ModuleRouteGuard>
          } />
          <Route path="attributes/:attributeKey" element={
            <ModuleRouteGuard moduleKey="attributes" redirectTo="/prioritization/settings">
              <AttributeDetail />
            </ModuleRouteGuard>
          } />
          <Route path="value-scales/:attributeKey" element={
            <ModuleRouteGuard moduleKey="attributes" redirectTo="/prioritization/settings">
              <AttributeDetail />
            </ModuleRouteGuard>
          } />
          <Route path="value-scales" element={
            <ModuleRouteGuard moduleKey="attributes" redirectTo="/prioritization/settings">
              <ValueScalesSettings />
            </ModuleRouteGuard>
          } />
          <Route path="queues" element={<Queues />} />
          <Route path="diagnostics" element={<DataDiagnostics />} />
          <Route path="attribute-permissions/:attributeKey" element={
            <ModuleRouteGuard moduleKey="attributes" redirectTo="/prioritization/settings">
              <AttributePermissionDetail />
            </ModuleRouteGuard>
          } />
          <Route path="attribute-permissions" element={
            <ModuleRouteGuard moduleKey="attributes" redirectTo="/prioritization/settings">
              <AttributePermissions />
            </ModuleRouteGuard>
          } />
          <Route path="authorization" element={<AuthorizationDemo />} />
          <Route path="agents" element={<AgentsPage />} />
          <Route path="custom-agents" element={<CustomAgentsPage />} />
          <Route path="custom-agents/:agentId" element={<CustomAgentDetailPage />} />
          <Route path="voices" element={<VoicesPage />} />
          <Route path="voices/:voiceId" element={<VoiceDetailPage />} />
          <Route path="ai-assists" element={<AiAssistsPage />} />
          <Route path="atc-agent" element={<AirTrafficController />} />
          <Route path="value-estimator-agent" element={<ValueEstimatorAgent />} />
          <Route path="twins-agent" element={<TwinsAgent />} />
          <Route path="scribe-agent" element={<ScribeAgent />} />
          <Route path="harry-agent" element={<HarryAgent />} />
          <Route path="wes-agent" element={<WesAgent />} />
          <Route path="nexus-agent" element={<NexusAgent />} />
          <Route path="fracture-agent" element={<TemplateAgentSettings def={CASSANDRA_AGENT} />} />
          <Route path="cassandra-agent" element={<Navigate to="/prioritization/settings/fracture-agent" replace />} />
          <Route path="compass-agent" element={<TemplateAgentSettings def={COMPASS_AGENT} />} />
          <Route path="keystone-agent" element={<TemplateAgentSettings def={KEYSTONE_AGENT} />} />
          <Route path="meridian-agent" element={<TemplateAgentSettings def={MERIDIAN_AGENT} />} />
          <Route path="condition-watch-agent" element={<TemplateAgentSettings def={CONDITION_WATCH_AGENT} />} />
          <Route path="beacon-agent" element={<TemplateAgentSettings def={BEACON_AGENT} />} />
          <Route path="review-due-agent" element={<TemplateAgentSettings def={REVIEW_DUE_AGENT} />} />
          <Route path="classification-report" element={<ClassificationProposalsReport />} />
          {/* Feature Flags - for developers/admins */}
          <Route path="features" element={<FeatureFlags />} />
          {isModuleFeatureAccessible(workspaceFeatures, 'backlog_health', FEATURE_FLAGS.BACKLOG_HEALTH) && (
            <Route path="backlog-health" element={<BacklogHealth />} />
          )}
          <Route path="archived-items" element={<ArchivedItems />} />
          <Route path="sources" element={<ItemSources />} />
          <Route path="categories" element={<ItemCategories />} />
          <Route path="modules" element={<Modules />} />
          <Route path="settings-transfer" element={<WorkspaceSettingsTransfer />} />
          <Route path="reports-config" element={
            <ModuleRouteGuard moduleKey="reports" redirectTo="/prioritization/settings">
              <ReportSettings />
            </ModuleRouteGuard>
          } />
          <Route path="my-profile" element={<MyProfile />} />
          <Route path="my-connectors" element={<MyConnectors />} />
          <Route path="change-password" element={<ChangePassword />} />
          {/* Terms & Definitions became Settings → Readiness → Every item (2026-10-15). */}
          <Route path="definition" element={<Navigate to="/prioritization/settings/readiness#every-item" replace />} />
          <Route path="readiness" element={<ReadinessDefinitions />} />
          <Route path="configurations" element={<ConfigurationsSettings />} />
          <Route path="work-types" element={
            <ModuleRouteGuard moduleKey="execution_recipe" featureFlagKey={FEATURE_FLAGS.EXECUTION_RECIPE} redirectTo="/prioritization/settings">
              <WorkTypesSettings />
            </ModuleRouteGuard>
          } />
          <Route path="launch-types" element={<LaunchTypesSettings />} />
          <Route path="investment-types" element={<InvestmentTypesSettings />} />
          <Route path="planning" element={<AdminPlanningObjects />} />
          <Route path="plan-of-record" element={<PlanOfRecordSettings />} />
          <Route path="resource-types" element={<ResourceTypesSettings />} />
          <Route path="planning-defaults" element={<PlanningDefaultsSettings />} />
          {/* Launch Profiles became Workload Templates (owner ruling 2026-10-15). */}
          <Route path="launch-profiles" element={<Navigate to="/prioritization/settings/workload-templates" replace />} />
          <Route path="type-patterns" element={<TypePatternsSettings />} />
          <Route
            path="workload-templates"
            element={
              <ModuleRouteGuard
                moduleKey="time_phased_planning"
                featureFlagKey={FEATURE_FLAGS.TIME_PHASED_PLANNING}
                redirectTo="/prioritization/settings/planning-engine"
              >
                <LaunchProfilesSettings />
              </ModuleRouteGuard>
            }
          />
          <Route
            path="predictability-profiles"
            element={
              <ModuleRouteGuard
                moduleKey="time_phased_planning"
                featureFlagKey={FEATURE_FLAGS.TIME_PHASED_PLANNING}
                redirectTo="/prioritization/settings/planning-engine"
              >
                <PredictabilityProfilesSettings />
              </ModuleRouteGuard>
            }
          />
          {/* Sandbox tools */}
          <Route path="sandbox/sector-map" element={<SectorMap />} />
          <Route path="sandbox" element={<Navigate to="/prioritization/settings/developer" replace />} />
          {/* Settings group index pages */}
          <Route path="my-account" element={<SettingsGroupPage groupKey="my-account" />} />
          <Route path="workspace-setup" element={<SettingsGroupPage groupKey="workspace-setup" />} />
          <Route path="workspace" element={<Navigate to="/prioritization/settings/workspace-setup" replace />} />
          <Route path="people-agents" element={<SettingsGroupPage groupKey="people-agents" />} />
          <Route path="groups-access" element={<SettingsGroupPage groupKey="groups-access" />} />
          <Route path="people-agents-access" element={<Navigate to="/prioritization/settings/people-agents" replace />} />
          <Route path="people" element={<Navigate to="/prioritization/settings/people-agents" replace />} />
          <Route path="permissions-policies" element={<Navigate to="/prioritization/settings/groups-access" replace />} />
          <Route path="workflows-templates" element={<SettingsGroupPage groupKey="workflows-templates" />} />
          <Route path="content" element={<SettingsGroupPage groupKey="content" />} />
          <Route path="patterns" element={<SettingsGroupPage groupKey="patterns" />} />
          <Route path="planning-engine" element={<SettingsGroupPage groupKey="planning-engine" />} />
          <Route path="integrations-sync" element={<SettingsGroupPage groupKey="integrations-sync" />} />
          <Route path="sync" element={<Navigate to="/prioritization/settings/integrations-sync" replace />} />
          <Route path="developer" element={<SettingsGroupPage groupKey="developer" />} />
        </Route>
        
        {/* ======================================== */}
        {/* ADMIN (legacy path, redirects to settings) */}
        {/* ======================================== */}
        <Route path="admin">
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<Navigate to="/prioritization/settings/users" replace />} />
          <Route path="groups" element={<Navigate to="/prioritization/settings/groups" replace />} />
          <Route path="roles" element={<Navigate to="/prioritization/settings/roles" replace />} />
          <Route path="permissions" element={<Navigate to="/prioritization/settings/permissions" replace />} />
          <Route path="identity" element={<Navigate to="/prioritization/settings/identity" replace />} />
          <Route path="connectors" element={<Navigate to="/prioritization/settings/connectors" replace />} />
        </Route>
        
        {/* ======================================== */}
        {/* DOCUMENTATION                           */}
        {/* ======================================== */}
        <Route path="documentation">
          {/* New hub replaces DocumentationIndex */}
          <Route index element={<DocHub />} />

          {/* Area-specific docs (new) */}
          <Route path="items" element={<ItemManagementDocs />} />
          <Route path="cycles" element={<CyclesDocs />} />
          <Route path="sessions" element={<SessionsDocs />} />
          <Route path="tracks" element={<TracksDocs />} />
          <Route path="catchball" element={<CatchballDocs />} />
          <Route path="api" element={<ApiReferenceDocs />} />
          <Route path="automations" element={<AutomationsDocs />} />

          {/* Existing pages — kept for backward compatibility */}
          <Route path="flows" element={<SystemFlows />} />
          <Route path="glossary" element={
            <DocumentationViewer
              filename="SYSTEM_TERMINOLOGY.md"
              title="Glossary"
              description="Master reference for all terms, concepts, and canonical definitions"
            />
          } />

          {/* Legacy routes — redirect to new locations */}
          {/* The User Guide renders again. public/USER_GUIDE.md was deleted as
              collateral in fc8507faf ("planning engine expansion…"), so this
              route was reduced to a redirect and the footer link quietly went
              nowhere. The file is restored from fc8507faf^ (1,972 lines). */}
          <Route
            path="user-guide"
            element={
              <DocumentationViewer
                filename="USER_GUIDE.md"
                title="User Guide"
                description="How the prioritization system works, end to end — concepts, lifecycle, roles, and workflows."
              />
            }
          />
          <Route path="terminology" element={<Navigate to="/prioritization/documentation/glossary" replace />} />
          <Route path="architecture" element={<Navigate to="/prioritization/documentation" replace />} />
          <Route path="roles" element={<Navigate to="/prioritization/documentation" replace />} />
          <Route path="design" element={<Navigate to="/prioritization/documentation" replace />} />
        </Route>
        
        {/* ======================================== */}
        {/* LEXICON — Language Intelligence         */}
        {/* ======================================== */}
        {isModuleFeatureAccessible(workspaceFeatures, 'lexicon', FEATURE_FLAGS.LEXICON) && (
          <>
            <Route path="lexicon" element={<LexiconPage />} />
            <Route path="lexicon/new" element={<LexiconTranscriptNew />} />
            <Route path="lexicon/profile/:speakerName" element={<LexiconSpeakerProfile />} />
            <Route path="lexicon/transcript/:transcriptId" element={<LexiconPage />} />
          </>
        )}

        {/* ======================================== */}
        {/* OTHER PAGES                             */}
        {/* ======================================== */}
        <Route path="item/:id" element={<RedirectItemToItemsDetail />} />
        
        {/* ======================================== */}
        {/* LEGACY ROUTES - Redirect to new paths   */}
        {/* v2.2 - Consolidated object-centered IA  */}
        {/* ======================================== */}
        
        {/* Shape/Score/Level → Items with filters */}
        <Route path="shaping" element={<Navigate to="/prioritization/items/needs-definition" replace />} />
        
        {/* Workspace routes → new locations */}
        <Route path="workspace" element={<Navigate to="/prioritization" replace />} />
        <Route path="workspace/intake" element={<Navigate to="/prioritization/intake/triage" replace />} />
        <Route path="workspace/submit" element={<Navigate to="/prioritization/intake/submit" replace />} />
        <Route path="workspace/intake-queue" element={<Navigate to="/prioritization/intake/triage" replace />} />
        <Route path="workspace/submissions/mine" element={<Navigate to="/prioritization/intake/mine" replace />} />
        <Route path="workspace/shaping" element={<Navigate to="/prioritization/items/needs-definition" replace />} />
        <Route path="workspace/attributes" element={<Navigate to="/prioritization/settings/attributes" replace />} />
        <Route path="workspace/lists" element={<Navigate to="/prioritization/lists" replace />} />
        <Route path="workspace/lists/:id" element={<Navigate to="/prioritization/lists/:id" replace />} />
        <Route path="workspace/drift" element={<Navigate to="/prioritization/monitor/drift" replace />} />
        <Route path="workspace/realignment" element={<Navigate to="/prioritization/monitor/realignment" replace />} />
        <Route path="workspace/map" element={<Navigate to="/prioritization/monitor/map" replace />} />
        <Route path="workspace/settings" element={<Navigate to="/prioritization/settings/users" replace />} />
        <Route path="workspace/analytics" element={<Navigate to="/prioritization/monitor/analytics" replace />} />
        <Route path="workspace/tools" element={<Navigate to="/prioritization/tools" replace />} />
        <Route path="workspace/tools/eisenhower" element={<Navigate to="/prioritization/tools/eisenhower-matrix" replace />} />
        
        {/* Process routes → cycles */}
        <Route path="processes" element={<Navigate to="/prioritization/cycles" replace />} />
        <Route path="processes/:processId/*" element={<Navigate to="/prioritization/cycles" replace />} />
        
        {/* Admin routes → settings */}
        <Route path="admin" element={<AdminDashboard />} />
        <Route path="admin/users" element={<Navigate to="/prioritization/settings/users" replace />} />
        <Route path="admin/groups" element={<Navigate to="/prioritization/settings/groups" replace />} />
        <Route path="admin/roles" element={<Navigate to="/prioritization/settings/roles" replace />} />
        <Route path="admin/settings" element={<Navigate to="/prioritization/settings/users" replace />} />
        <Route path="admin/security" element={<Navigate to="/lab/prioritization-archive/security" replace />} />
        <Route path="admin/strategy" element={<Navigate to="/prioritization/strategy/alignment" replace />} />
        <Route path="admin/decisions" element={<Navigate to="/prioritization/strategy/decisions" replace />} />
        <Route path="admin/goals" element={<Navigate to="/prioritization/settings/goals" replace />} />
        
        {/* Old single-word routes */}
        <Route path="intake" element={<Navigate to="/prioritization/intake/triage" replace />} />
        <Route path="attributes" element={<Navigate to="/prioritization/settings/attributes" replace />} />
        <Route path="criteria" element={<Navigate to="/prioritization/settings/attributes" replace />} />
        <Route path="attribute-permissions" element={<Navigate to="/prioritization/settings/attribute-permissions" replace />} />
        <Route path="attribute-permissions/:attributeKey" element={<RedirectLegacyAttributePermissionDetail />} />
        <Route path="lists" element={<Navigate to="/prioritization/lists" replace />} />
        <Route path="drift" element={<Navigate to="/prioritization/monitor/drift" replace />} />
        <Route path="realignment" element={<Navigate to="/prioritization/monitor/realignment" replace />} />
        <Route path="map" element={<Navigate to="/prioritization/monitor/map" replace />} />
        <Route path="analytics" element={<Navigate to="/prioritization/monitor/analytics" replace />} />
        <Route path="tools" element={<Navigate to="/prioritization/tools" replace />} />
        <Route path="docs" element={<Navigate to="/prioritization/documentation" replace />} />
        <Route path="board" element={<Navigate to="/prioritization/lists" replace />} />
        
        {/* Catch-all */}
        <Route path="*" element={<CatchAllRedirect />} />
      </Routes>
    </PrioritizationLayout>
  );
}

export default PrioritizationAppContent;
